import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CustomModal from '@/components/atoms/CustomModal';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import TitleText from '@/components/atoms/TitleText';

export interface FeedbackFilterValues {
  orderType: 'BIDDING' | 'MARKETPLACE' | '';
  dateFrom: string;
  dateTo: string;
}

interface FormInternalValues {
  orderType: 'BIDDING' | 'MARKETPLACE' | '';
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

interface FeedbackFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FeedbackFilterValues;
  onApplyFilters: (filters: FeedbackFilterValues) => void;
  'data-testid'?: string;
}

export const FeedbackFilterModal: React.FC<FeedbackFilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  'data-testid': dataTestId = 'logistics-feedback-filter-modal',
}) => {
  const { t } = useTranslation();

  const schema = z
    .object({
      orderType: z.enum(['BIDDING', 'MARKETPLACE', '']).optional(),
      dateFrom: z.custom<Dayjs>().nullable().optional(),
      dateTo: z.custom<Dayjs>().nullable().optional(),
    })
    .superRefine(({ dateFrom, dateTo }, ctx) => {
      const today = dayjs().endOf('day');
      if (dateFrom && dateFrom.isAfter(today, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('biddingPosts.dateFutureError'),
          path: ['dateFrom'],
        });
      }
      if (dateTo && dateTo.isAfter(today, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('biddingPosts.dateFutureError'),
          path: ['dateTo'],
        });
      }
      if (dateFrom && dateTo && dateTo.isBefore(dateFrom, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('dispute.dateToBeforeFrom'),
          path: ['dateTo'],
        });
      }
    });

  const methods = useForm<FormInternalValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      orderType: '',
      dateFrom: null,
      dateTo: null,
    },
  });

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        orderType: filters.orderType || '',
        dateFrom: filters.dateFrom ? dayjs(filters.dateFrom) : null,
        dateTo: filters.dateTo ? dayjs(filters.dateTo) : null,
      });
    }
  }, [isOpen, filters, methods]);

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    Boolean(watchAll.orderType),
    Boolean(watchAll.dateFrom),
    Boolean(watchAll.dateTo),
  ].filter(Boolean).length;

  const disabledDateFrom = (current: Dayjs) => {
    if (!current) return false;
    const isFuture = current.isAfter(dayjs(), 'day');
    const isAfterDateTo =
      !!watchAll.dateTo && current.isAfter(watchAll.dateTo, 'day');
    return isFuture || isAfterDateTo;
  };

  const disabledDateTo = (current: Dayjs) => {
    if (!current) return false;
    const isFuture = current.isAfter(dayjs(), 'day');
    const isBeforeDateFrom =
      !!watchAll.dateFrom && current.isBefore(watchAll.dateFrom, 'day');
    return isFuture || isBeforeDateFrom;
  };

  const onReset = () => {
    methods.reset({
      orderType: '',
      dateFrom: null,
      dateTo: null,
    });
    onApplyFilters({
      orderType: '',
      dateFrom: '',
      dateTo: '',
    });
    onClose();
  };

  const onSubmit = (values: FormInternalValues) => {
    const fromDateStr = values.dateFrom
      ? values.dateFrom.format('YYYY-MM-DD')
      : '';
    const toDateStr = values.dateTo ? values.dateTo.format('YYYY-MM-DD') : '';

    onApplyFilters({
      orderType: values.orderType || '',
      dateFrom: fromDateStr,
      dateTo: toDateStr,
    });
    onClose();
  };

  const orderTypeOptions = [
    { label: t('logisticsFeedback.typeBidding'), value: 'BIDDING' },
    { label: t('logisticsFeedback.typeMarketplace'), value: 'MARKETPLACE' },
  ];

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('logisticsFeedback.filterButton')}
      subTitle={
        <div
          className="flex items-center gap-2"
          data-testid={`${dataTestId}-subtitle-wrapper`}
        >
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied')}`}
            data-testid={`${dataTestId}-count`}
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold cursor-pointer"
            data-testid={`${dataTestId}-reset-button`}
          >
            {t('home.resetFilter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-6"
          data-testid={`${dataTestId}-form`}
        >
          {/* Order Type Section */}
          <div
            className="space-y-2"
            data-testid={`${dataTestId}-order-type-section`}
          >
            <TitleText
              text={t('logisticsFeedback.filterByType')}
              size="base"
              className="!font-semibold !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-order-type-title`}
            />
            <FormField
              name="orderType"
              id="orderType"
              type="select"
              options={orderTypeOptions}
              allowClear
              placeholder={t('logisticsFeedback.selectOrderType')}
              data-testid={`${dataTestId}-order-type-select`}
            />
          </div>

          <div
            className="border-t border-light-input-border dark:border-dark-input-border"
            data-testid={`${dataTestId}-divider`}
          />

          {/* Date Range Section */}
          <div
            className="grid grid-cols-2 gap-x-4"
            data-testid={`${dataTestId}-dates-section`}
          >
            <FormField
              name="dateFrom"
              id="dateFrom"
              type="date"
              label={t('home.dateFrom')}
              placeholder="dd/mm/yyyy"
              allowClear
              disabledDate={disabledDateFrom}
              maxDate={dayjs()}
              data-testid={`${dataTestId}-date-from`}
            />
            <FormField
              name="dateTo"
              id="dateTo"
              type="date"
              label={t('home.dateTo')}
              placeholder="dd/mm/yyyy"
              allowClear
              disabledDate={disabledDateTo}
              maxDate={dayjs()}
              data-testid={`${dataTestId}-date-to`}
            />
          </div>

          {/* Actions */}
          <div
            className="flex items-center justify-end gap-x-4 pt-4"
            data-testid={`${dataTestId}-actions`}
          >
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[44px]"
              data-testid={`${dataTestId}-cancel-button`}
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[44px]"
              data-testid={`${dataTestId}-save-button`}
            >
              {t('home.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
};
